import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MANAGE_FINANCE_DETAILS_ACCESS } from 'constants/userOperations';
import { cloneDeep } from 'lodash';
import { zipObject } from 'lodash/array';
import { reduce } from 'lodash/collection';
import { isEmpty } from 'lodash/lang';
import { userHasAccessTo, userHasRole } from 'services/auth';

import { INITIAL_PATIENT_STATUSES } from 'components/pages/patient-source/Patients/constants';
import { reshapeAndOrderSourceData } from 'components/pages/patient-source/Patients/PatientInfo/patientInfoService';

import { PatientSourceApi, StudySiteApi } from '../../../../../../../api';
import PatientApi from '../../../../../../../api/patient/PatientApi';
import ModalBoxes, { useModalBoxes } from '../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../../common/general/Button';
import ButtonGroup from '../../../../../../../common/general/ButtonGroup';
import useAppInfo from '../../../../../../../common/hooks/useAppInfo';
import { CANCELED, CLOSED, TREATMENT } from '../../../../../../../constants/ssuStatuses';
import { ROLE_SYSTEM_ADMINISTRATOR } from '../../../../../../../constants/userRoles';
import { NSPP_PATIENT } from '../../../../../../../services/i18n/namespaces';
import StudySiteStatusSourceSelect from '../StudySiteStatusSourceSelect';

import FinanceDetails from './FinanceDetails/FinanceDetails';
import GuardianDetails from './GuardianDetails/GuardianDetails';
import PatientContactDetails from './PatientContactDetails/PatientContactDetails';
import PatientDemographics from './PatientDemographics/PatientDemographics';
import PatientDetails from './PatientDetails/PatientDetails';
import initiateAddPatient from './services/initiateAddPatient';
import initiateEditPatient from './services/initiateEditPatient';
import { useInfo, useRequiredFields } from './AddOrEditPatientInfoModalHooks';

import './AddOrEditPatientInfoModal.scss';

const preScreenedStatus = INITIAL_PATIENT_STATUSES.find(status => status.id === 'Pre-screened');

export default function AddOrEditPatientInfoModal({
  currentStudy,
  patientStipendFieldsRequired,
  modalBox,
  patientInfo,
  onSave,
  onDuplicate
}) {
  const { t } = useTranslation();
  const appInfo = useAppInfo();
  const { setLoading } = useModalBoxes();
  const {
    info,
    isEditMode,
    validationMessages,
    setValidationMessages,
    handleCountryChange,
    handleStateChange,
    handleDateOfBirthChange,
    handleGuardianDobChange,
    handlePhoneNumberChange,
    handleInputChange,
    handleSexChange,
    handlePronounsChange,
    handlePrimaryLanguageChange,
    handlePreferredContactMethodChange,
    handleHeightChange,
    handleRacesChange,
    handleEthnicitiesChange,
    resetValidationMessageByName,
    handleChangeSmsOptInDropdown,
    handleChangeInterestedInFutureResearch,
    handlePaymentTypeChange,
    handleChangeDigitalPaymentAllowed,
    handleChangeOptionalSsnAllowed
  } = useInfo(patientInfo);

  const globalAddressesEnabled = appInfo?.features?.globalAddressesEnabled;
  const existingSsn = patientInfo?.ssn;
  const { address, cardId, paymentType } = info;

  const [source, setSource] = useState(null);
  const [otherSource, setOtherSource] = useState(null);
  const [status, setStatus] = useState(null);
  const [studySites, setStudySites] = useState([]);
  const [initialSelectedSsu, setInitialSelectedSsu] = useState(null);
  const [sources, setSources] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [medicalRecordNumber, setMedicalRecordNumber] = useState('');
  const [userCanManageFinanceDetails, setUserCanManageFinanceDetails] = useState(true);

  const [originalPID] = useState(cloneDeep(cardId));
  const [originalPaymentType] = useState(cloneDeep(paymentType));
  const [fieldsDataDictionary, setFieldsDataDictionary] = useState(null);
  const [fieldsDataDictionaryMap, setFieldsDataDictionaryMap] = useState({});

  const [isRequiredField] = useRequiredFields(
    info,
    originalPID,
    isEditMode ? patientStipendFieldsRequired : selectedSite?.patientStipendFieldsRequired
  );

  const { ethnicityCodes, raceCodes } = fieldsDataDictionaryMap;

  useEffect(() => {
    PatientApi.getDictionaries().then(({ data }) => {
      setFieldsDataDictionaryMap(prepareDictionaries(data));
      setFieldsDataDictionary(data);
    });
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      StudySiteApi.getAllStudySitesAndMap().then(({ data: studySites }) => {
        const ssus = studySites.filter(
          ({ siteStatus }) => userHasRole(ROLE_SYSTEM_ADMINISTRATOR) || ![TREATMENT, CLOSED].includes(siteStatus)
        );
        setStudySites(ssus);
        const notCanceledSsus = ssus.filter(({ siteStatus }) => CANCELED !== siteStatus);
        if (notCanceledSsus.length === 1) {
          setInitialSelectedSsu(notCanceledSsus[0]);
        }
      });
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      PatientSourceApi.getAllAssignmentPatientSources().then(({ data }) => {
        const reshapedSources = reshapeAndOrderSourceData(data);
        setSources(reshapedSources);
        setOtherSource(reshapedSources.find(source => source.source === 'Other - Other'));
      });
    }
  }, [isEditMode]);

  useEffect(() => {
    if (userHasAccessTo(MANAGE_FINANCE_DETAILS_ACCESS) && !isEditMode) {
      setUserCanManageFinanceDetails(false);
      if (!isEmpty(selectedSite) && !isEmpty(selectedStudy))
        PatientApi.checkAbilityToAddFinanceDetails(selectedSite.uniqueIdentifier).then(({ data }) => {
          if (data) {
            setUserCanManageFinanceDetails(data);
          }
        });
    }
  }, [isEditMode, selectedSite, selectedStudy]);

  useEffect(() => {
    if (userHasAccessTo(MANAGE_FINANCE_DETAILS_ACCESS) && isEditMode) {
      setUserCanManageFinanceDetails(false);
      PatientApi.checkAbilityToEditFinanceDetails(patientInfo.id).then(({ data }) => {
        if (data) {
          setUserCanManageFinanceDetails(data);
        }
      });
    }
  }, [isEditMode, patientInfo?.id]);

  return (
    <>
      <ModalBoxes.Body>
        {!isEditMode && (
          <>
            <div className="row">
              <div className="col-12 apm-form-label">{t('addOrEdit.selectSsu', { ns: NSPP_PATIENT })}</div>
            </div>
            <div className="col-8 top-selectors">
              <StudySiteStatusSourceSelect
                ssuProvider={() => studySites}
                ssuIdProvider={() => initialSelectedSsu?.uniqueIdentifier}
                onSSUFilterChange={onSSUFilterChange}
                getValidationMessage={getValidationMessage}
                sources={sources}
                setSource={v => {
                  resetValidationMessageByName('source');
                  setSource(v);
                }}
                source={source}
                setStatus={v => {
                  resetValidationMessageByName('status');
                  setStatus(v);
                }}
                status={status}
                className="aoepim-study-site-status-source-select"
              />
            </div>
          </>
        )}
        <PatientDetails
          isEditMode={isEditMode}
          handleDateOfBirthChange={handleDateOfBirthChange}
          handleInputChange={handleInputChange}
          handleSexChange={handleSexChange}
          handlePronounsChange={handlePronounsChange}
          handlePrimaryLanguageChange={handlePrimaryLanguageChange}
          isRequiredField={isRequiredField}
          getValidationMessage={getValidationMessage}
          medicalRecordNumber={medicalRecordNumber}
          setMedicalRecordNumber={mrn => {
            resetValidationMessageByName('medicalRecordNumber');
            setMedicalRecordNumber(mrn);
          }}
          info={info}
          fieldsDataDictionary={fieldsDataDictionary}
        />
        <PatientContactDetails
          handlePhoneNumberChange={handlePhoneNumberChange}
          handleCountryChange={handleCountryChange}
          handleStateChange={handleStateChange}
          handleInputChange={handleInputChange}
          handlePreferredContactMethodChange={handlePreferredContactMethodChange}
          isRequiredField={isRequiredField}
          getValidationMessage={getValidationMessage}
          address={address}
          info={info}
          fieldsDataDictionary={fieldsDataDictionary}
          handleChangeSmsOptInDropdown={handleChangeSmsOptInDropdown}
          handleChangeInterestedInFutureResearch={handleChangeInterestedInFutureResearch}
        />
        <PatientDemographics
          info={info}
          isRequiredField={isRequiredField}
          getValidationMessage={getValidationMessage}
          handleInputChange={handleInputChange}
          handleHeightChange={handleHeightChange}
          handleRacesChange={handleRacesChange}
          handleEthnicitiesChange={handleEthnicitiesChange}
          ethnicityCodes={ethnicityCodes}
          raceCodes={raceCodes}
        />
        {!globalAddressesEnabled && (
          <FinanceDetails
            handleInputChange={handleInputChange}
            isRequiredField={isRequiredField}
            getValidationMessage={getValidationMessage}
            info={info}
            userCanManageFinanceDetails={userCanManageFinanceDetails}
            handlePaymentTypeChange={handlePaymentTypeChange}
            originalPaymentType={originalPaymentType}
          />
        )}
        <GuardianDetails
          handleInputChange={handleInputChange}
          handlePhoneNumberChange={handlePhoneNumberChange}
          handleGuardianDobChange={handleGuardianDobChange}
          getValidationMessage={getValidationMessage}
          info={info}
        />
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <ButtonGroup>
          <Button priority="medium" onClick={modalBox.close}>
            {t('CANCEL')}
          </Button>
          <Button onClick={update}>{t('SAVE')}</Button>
        </ButtonGroup>
      </ModalBoxes.Footer>
    </>
  );

  function getValidationMessage(field) {
    return validationMessages[field];
  }

  function onSSUFilterChange(ssus, study, site) {
    handleChangeDigitalPaymentAllowed(!!study?.digitalPatientPayment);
    handleChangeOptionalSsnAllowed(!!study?.optionalPatientSsn);
    resetValidationMessageByName('study');
    setSelectedStudy(study);
    if (site) {
      resetValidationMessageByName('site');
      const ssu = ssus.find(e => e.site.uniqueIdentifier === site.uniqueIdentifier);
      setSelectedSite(ssu);
      if (ssu.patientStipendFieldsRequired) {
        setStatus(preScreenedStatus);
        setSource(otherSource);
      }
    } else {
      setSelectedSite(null);
    }
  }

  function update() {
    if (isEditMode) {
      initiateEditPatient({
        info,
        currentStudy,
        patientInfo,
        setValidationMessages,
        setLoading,
        postSave() {
          onSave();
        },
        onDuplicate,
        modalBox,
        originalPID,
        patientStipendFieldsRequired,
        existingSsn
      });
      return;
    }
    initiateAddPatient({
      info,
      ssu: { study: selectedStudy, site: selectedSite, source: source, status: status },
      medicalRecordNumber,
      setValidationMessages,
      setLoading,
      postSave() {
        onSave();
      }
    });
  }
}

AddOrEditPatientInfoModal.className = 'add-or-edit-patient-info-modal';
AddOrEditPatientInfoModal.size = 'w1100';

function prepareDictionaries(data) {
  return reduce(
    data,
    function(accumulator, value, key) {
      accumulator[key] = reduce(
        value,
        function(accumulator, value) {
          const disabledOptions = value?.disabledIds || [];
          accumulator[value.id] = {
            ...value,
            disabledIds: !isEmpty(disabledOptions)
              ? zipObject(disabledOptions, Array(disabledOptions.length).fill(true))
              : null
          };
          return accumulator;
        },
        {}
      );
      return accumulator;
    },
    {}
  );
}
